(() => {
  const rootUrl = document.body?.dataset.rootUrl || '/';
  const route = (path) => `${rootUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  const currencyCode = () => window.Shopify?.currency?.active || document.body?.dataset.currency || 'USD';
  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currencyCode()
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const setCartCount = (count) => qsa('[data-cart-count]').forEach((node) => { node.textContent = count; });
  const announce = (message) => {
    const live = qs('[data-cart-live]');
    if (live) live.textContent = message;
  };

  async function getCart() {
    const response = await fetch(route('cart.js'), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart.');
    return response.json();
  }

  function makeCartItem(item, index) {
    const wrapper = document.createElement('div');
    wrapper.className = 'cart-item';
    wrapper.dataset.cartKey = item.key;

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.product_title || '';
      image.loading = 'lazy';
      wrapper.appendChild(image);
    }

    const content = document.createElement('div');
    const title = document.createElement('a');
    title.href = item.url;
    title.textContent = item.product_title || item.title || '';
    content.appendChild(title);

    if (item.variant_title) {
      const variant = document.createElement('p');
      variant.textContent = item.variant_title;
      content.appendChild(variant);
    }

    const price = document.createElement('p');
    price.textContent = formatMoney(item.final_line_price);
    content.appendChild(price);

    const controls = document.createElement('div');
    controls.className = 'cart-item__controls';

    const label = document.createElement('label');
    label.className = 'sr-only';
    label.htmlFor = `DrawerQtyDynamic-${index}`;
    label.textContent = 'Quantity';
    controls.appendChild(label);

    const quantity = document.createElement('input');
    quantity.id = `DrawerQtyDynamic-${index}`;
    quantity.type = 'number';
    quantity.min = '0';
    quantity.value = item.quantity;
    quantity.dataset.cartQuantity = '';
    quantity.dataset.key = item.key;
    controls.appendChild(quantity);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'button';
    remove.dataset.cartRemove = '';
    remove.dataset.key = item.key;
    remove.textContent = 'Remove';
    controls.appendChild(remove);

    content.appendChild(controls);
    wrapper.appendChild(content);
    return wrapper;
  }

  async function refreshCart() {
    const cart = await getCart();
    setCartCount(cart.item_count);

    const itemsRoot = qs('[data-cart-items]');
    if (itemsRoot) {
      itemsRoot.replaceChildren();
      if (cart.items.length) {
        cart.items.forEach((item, index) => itemsRoot.appendChild(makeCartItem(item, index)));
      } else {
        const empty = document.createElement('p');
        empty.textContent = 'Your bag is empty.';
        itemsRoot.appendChild(empty);
      }
    }

    const subtotal = qs('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

    const checkout = qs('[data-cart-checkout]');
    if (checkout) checkout.disabled = cart.item_count === 0;
    return cart;
  }

  let previouslyFocused = null;
  function openDrawer() {
    const drawer = qs('#CartDrawer');
    if (!drawer) return;
    previouslyFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    qs('.drawer__panel', drawer)?.focus();
  }

  function closeDrawer() {
    const drawer = qs('#CartDrawer');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
    if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
  }

  async function addToCart(form) {
    const response = await fetch(route('cart/add.js'), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || 'Unable to add this item.');
    await refreshCart();
    openDrawer();
    announce('Item added to cart.');
  }

  async function changeCart(key, quantity) {
    const response = await fetch(route('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || 'Unable to update cart.');
    await refreshCart();
    announce('Cart updated.');
  }

  function selectedOptions(form) {
    return qsa('.variant-picker fieldset', form).map((fieldset) => qs('input:checked', fieldset)?.value || '');
  }

  function initProductForm(form) {
    if (form.dataset.productInitialized === 'true') return;
    form.dataset.productInitialized = 'true';

    const variantsNode = qs('[data-product-variants]', form);
    const select = qs('[data-variant-select]', form);
    const addButton = qs('[data-add-to-cart]', form);
    const addText = qs('[data-add-to-cart-text]', form);
    if (!variantsNode || !select || !addButton || !addText) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsNode.textContent || '[]');
    } catch (_) {
      return;
    }

    form.addEventListener('change', (event) => {
      if (!event.target.matches('.variant-picker input')) return;
      const options = selectedOptions(form);
      const variant = variants.find((candidate) => candidate.options.every((option, index) => option === options[index]));

      if (!variant) {
        select.value = '';
        addButton.disabled = true;
        addText.textContent = 'Unavailable';
        return;
      }

      select.value = String(variant.id);
      addButton.disabled = !variant.available;
      addText.textContent = variant.available ? 'Add to cart' : 'Sold out';

      const scope = form.closest('.product-info, .featured-product') || form.parentElement;
      const priceRoot = scope ? qs('[data-product-price]', scope) : null;
      if (priceRoot) {
        const price = qs('[data-current-price]', priceRoot);
        const compare = qs('[data-compare-price]', priceRoot);
        if (price) price.textContent = formatMoney(variant.price);
        if (compare) {
          const showCompare = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
          compare.hidden = !showCompare;
          compare.textContent = showCompare ? formatMoney(variant.compare_at_price) : '';
        }
      }

      if (form.closest('.product-main')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    });
  }

  function initReveal(root = document) {
    const elements = qsa('.reveal:not([data-reveal-initialized])', root);
    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    elements.forEach((element) => {
      element.dataset.revealInitialized = 'true';
      observer.observe(element);
    });
  }

  document.addEventListener('click', async (event) => {
    const open = event.target.closest('[data-drawer-open]');
    if (open) {
      event.preventDefault();
      try { await refreshCart(); } catch (_) {}
      openDrawer();
      return;
    }

    if (event.target.closest('[data-drawer-close]')) {
      closeDrawer();
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-action]');
    if (quantityButton) {
      const control = quantityButton.closest('[data-quantity-control]');
      const input = control ? qs('input[name="quantity"]', control) : null;
      if (!input) return;
      const delta = quantityButton.dataset.quantityAction === 'plus' ? 1 : -1;
      input.value = String(Math.max(1, Number(input.value || 1) + delta));
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      remove.disabled = true;
      try {
        await changeCart(remove.dataset.key, 0);
      } catch (error) {
        announce(error.message);
      } finally {
        remove.disabled = false;
      }
    }
  });

  document.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    input.disabled = true;
    try {
      await changeCart(input.dataset.key, Math.max(0, Number(input.value || 0)));
    } catch (error) {
      announce(error.message);
      try { await refreshCart(); } catch (_) {}
    } finally {
      input.disabled = false;
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-ajax-cart]')) return;
    event.preventDefault();
    const errorNode = qs('[data-form-error]', form);
    if (errorNode) {
      errorNode.hidden = true;
      errorNode.textContent = '';
    }
    const submit = qs('[type="submit"]', form);
    const submitWasDisabled = submit ? submit.disabled : false;
    if (submit) submit.disabled = true;
    try {
      await addToCart(form);
    } catch (error) {
      if (errorNode) {
        errorNode.hidden = false;
        errorNode.textContent = error.message;
      }
    } finally {
      if (submit) submit.disabled = submitWasDisabled;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && qs('#CartDrawer.is-open')) closeDrawer();
  });

  qsa('[data-product-form]').forEach(initProductForm);
  initReveal();

  document.addEventListener('shopify:section:load', (event) => {
    qsa('[data-product-form]', event.target).forEach(initProductForm);
    initReveal(event.target);
  });
})();


/* SAVED SOCIETY HOMEPAGE MOTION — v1.0.4 */
(() => {
  const qs=(s,r=document)=>r.querySelector(s),qsa=(s,r=document)=>Array.from(r.querySelectorAll(s));
  const clamp=(n,min=0,max=1)=>Math.min(max,Math.max(min,n));
  const reduced=()=>window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const progressFor=el=>{const r=el.getBoundingClientRect(),vh=window.innerHeight||1;return clamp((vh-r.top)/(vh+r.height));};
  const visible=el=>{const r=el.getBoundingClientRect();return r.bottom>-180&&r.top<innerHeight+180;};
  let ticking=false;
  function renderMotion(){
    ticking=false;if(reduced())return;
    qsa('[data-ss-hero]').forEach(el=>{if(!visible(el))return;const p=progressFor(el),lines=qsa('.ss-home-hero__line',el);if(lines[0])lines[0].style.transform=`translate3d(${-p*35}px,0,0)`;if(lines[1])lines[1].style.transform=`translate3d(${p*46}px,0,0)`;});
    qsa('[data-kinetic-manifesto]').forEach(el=>{if(!visible(el))return;const p=progressFor(el);qsa('.ss-home-manifesto__line',el).forEach((line,i)=>{const start=i%2===0?36:-34;line.style.transform=`translate3d(${(1-p)*start}px,0,0)`;line.style.opacity=String(.25+p*.75);});const orbit=qs('.ss-home-manifesto__orbit',el),star=qs('.ss-home-manifesto__star',el);if(orbit)orbit.style.transform=`translate(-50%,-50%) rotate(${-8+p*8}deg)`;if(star)star.style.transform=`rotate(${p*35}deg)`;});
    qsa('[data-world-motion]').forEach(el=>{if(!visible(el))return;const p=progressFor(el),art=qs('.ss-home-world__art',el),symbol=qs('.ss-home-world__symbol',el);if(art)art.style.transform=`translate(-50%,-50%) scale(${.93+p*.08}) rotate(${(p-.5)*2.2}deg)`;if(symbol)symbol.style.transform=`rotate(${p*22}deg)`;});
    qsa('[data-statement-motion]').forEach(el=>{if(!visible(el))return;const p=progressFor(el),w=window.innerWidth,phase=p-.5,travel=w*.34,top=qs('.ss-home-statement__line--top',el),bottom=qs('.ss-home-statement__line--bottom',el),star=qs('.ss-home-statement__star',el);if(top)top.style.transform=`translate3d(calc(-50% + ${(-phase*travel).toFixed(2)}px),0,0)`;if(bottom)bottom.style.transform=`translate3d(calc(-50% + ${(phase*travel).toFixed(2)}px),0,0)`;if(star)star.style.transform=`rotate(${p*40}deg)`;});
    qsa('[data-scroll-shift]').forEach(el=>{if(!visible(el))return;const r=el.getBoundingClientRect(),factor=parseFloat(el.dataset.scrollShift||'0.06'),delta=r.top+r.height/2-innerHeight/2,shift=clamp(-delta*factor,-70,70);el.style.setProperty('--ss-shift',`${shift.toFixed(2)}px`);});
  }
  function requestMotion(){if(!ticking){ticking=true;requestAnimationFrame(renderMotion);}}
  function initCarousel(root=document){qsa('[data-collection-carousel]:not([data-carousel-ready])',root).forEach(section=>{section.dataset.carouselReady='true';const track=qs('[data-carousel-track]',section);if(!track)return;const prev=qs('[data-carousel-prev]',section),next=qs('[data-carousel-next]',section),amount=()=>Math.max(280,track.clientWidth*.72);prev?.addEventListener('click',()=>track.scrollBy({left:-amount(),behavior:'smooth'}));next?.addEventListener('click',()=>track.scrollBy({left:amount(),behavior:'smooth'}));let dragging=false,startX=0,startScroll=0,moved=false;track.addEventListener('pointerdown',e=>{if(e.pointerType==='touch')return;dragging=true;moved=false;startX=e.clientX;startScroll=track.scrollLeft;track.classList.add('is-dragging');track.setPointerCapture?.(e.pointerId);});track.addEventListener('pointermove',e=>{if(!dragging)return;const dx=e.clientX-startX;if(Math.abs(dx)>5)moved=true;track.scrollLeft=startScroll-dx;});const end=e=>{if(!dragging)return;dragging=false;track.classList.remove('is-dragging');try{track.releasePointerCapture?.(e.pointerId);}catch(_){}};track.addEventListener('pointerup',end);track.addEventListener('pointercancel',end);track.addEventListener('click',e=>{if(moved){e.preventDefault();e.stopPropagation();moved=false;}},true);track.addEventListener('keydown',e=>{if(e.key==='ArrowRight'){e.preventDefault();track.scrollBy({left:amount(),behavior:'smooth'});}if(e.key==='ArrowLeft'){e.preventDefault();track.scrollBy({left:-amount(),behavior:'smooth'});}});});}
  function init(root=document){initCarousel(root);requestMotion();}
  addEventListener('scroll',requestMotion,{passive:true});addEventListener('resize',requestMotion,{passive:true});
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init());else init();
  document.addEventListener('shopify:section:load',e=>init(e.target));
})();

/* ==========================================================
   SAVED SOCIETY — ABOUT PAGE MOTION
   ========================================================== */

function initSavedSocietyAbout(root = document) {
  const about = root.querySelector('.ss-about');

  if (!about) return;

  requestAnimationFrame(() => {
    about.classList.add('is-ready');
  });

  const elements = about.querySelectorAll('[data-about-reveal]');

  if (!('IntersectionObserver' in window)) {
    elements.forEach((element) => {
      element.classList.add('is-visible');
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const element = entry.target;

        const delay = Number(
          element.getAttribute('data-delay') || 0
        );

        element.style.setProperty(
          '--about-delay',
          `${delay}ms`
        );

        element.classList.add('is-visible');

        observer.unobserve(element);
      });
    },
    {
      threshold: 0.14,
      rootMargin: '0px 0px -8% 0px'
    }
  );

  elements.forEach((element) => {
    observer.observe(element);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initSavedSocietyAbout(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initSavedSocietyAbout(event.target);
});